import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import axiosInstance from '../../api/axiosConfig';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';
import { REASON_LABEL } from './clearanceTemplate';
import CreateClearanceModal from './CreateClearanceModal';
import ClearanceDetail from './ClearanceDetail';
import EmployeeMasterTab from './EmployeeMasterTab';
import NotificationConfigPanel from '../common/NotificationConfigPanel';
import './ClearanceForm.css';

const STATUS_LABEL = {
    'in-progress': 'In Progress',
    'cleared': 'Cleared',
    'cancelled': 'Cancelled'
};

const ClearanceForm = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const isCoordinator = useMemo(() => (
        user?.role === 'system_admin' ||
        hasCardAction(user?.permissions, 'clearance-form', 'approve')
    ), [user]);

    const [view, setView] = useState('list');          // 'list' | 'detail'
    const [selectedId, setSelectedId] = useState(null);

    // 'requests' | 'employees' | 'reminders'. Employee master and reminder
    // settings are coordinator-only; the tab strip is hidden entirely for
    // everyone else so the page looks unchanged.
    const [tab, setTab] = useState('requests');

    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [error, setError] = useState('');

    const fetchForms = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (statusFilter) params.append('status', statusFilter);
            params.append('limit', '50');
            const res = await axiosInstance.get(`/api/clearance?${params.toString()}`);
            if (res.data?.success) setForms(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load clearance forms');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter]);

    useEffect(() => {
        if (view === 'list' && tab === 'requests') fetchForms();
    }, [view, tab, fetchForms]);

    const openDetail = (id) => {
        setSelectedId(id);
        setView('detail');
    };

    const backToList = () => {
        setSelectedId(null);
        setView('list');
    };

    const sectionProgress = (form) => {
        const total = form.sections?.length || 0;
        const done = (form.sections || []).filter(s => s.status === 'completed').length;
        return `${done}/${total}`;
    };

    if (view === 'detail' && selectedId) {
        return (
            <ClearanceDetail
                formId={selectedId}
                isCoordinator={isCoordinator}
                onBack={backToList}
            />
        );
    }

    return (
        <div className="clearance-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="clearance-content">
                <div className="page-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="page-header-text">
                        <h1>Clearance Form</h1>
                        <p>Employee exit / transfer clearance — section sign-off</p>
                    </div>
                    <div className="page-header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                            <span className="user-role">({user?.role})</span>
                        </div>
                        <button onClick={() => navigate('/purpose-selection')} className="btn-back">
                            ← Back
                        </button>
                    </div>
                </div>

                {isCoordinator && (
                    <div className="clearance-tabs">
                        <button
                            className={`clearance-tab ${tab === 'requests' ? 'is-active' : ''}`}
                            onClick={() => setTab('requests')}
                        >
                            Clearance Requests
                        </button>
                        <button
                            className={`clearance-tab ${tab === 'employees' ? 'is-active' : ''}`}
                            onClick={() => setTab('employees')}
                        >
                            Employee Master
                        </button>
                        <button
                            className={`clearance-tab ${tab === 'reminders' ? 'is-active' : ''}`}
                            onClick={() => setTab('reminders')}
                        >
                            Configure
                        </button>
                    </div>
                )}

                {tab === 'employees' && isCoordinator ? <EmployeeMasterTab /> :
                 tab === 'reminders' && isCoordinator ? <NotificationConfigPanel module="clearance-form" /> : (
                <>
                <div className="clearance-toolbar">
                    <div className="toolbar-left">
                        <input
                            type="text"
                            className="clearance-search"
                            placeholder="Search by ID, employee name or staff code…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchForms()}
                        />
                        <select
                            className="clearance-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            <option value="in-progress">In Progress</option>
                            <option value="cleared">Cleared</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <button className="btn btn-secondary" onClick={fetchForms}>Search</button>
                    </div>
                    {isCoordinator && (
                        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                            + New Clearance Request
                        </button>
                    )}
                </div>

                {error && <div className="clearance-error">{error}</div>}

                <div className="clearance-list card">
                    {loading ? (
                        <div className="clearance-empty">Loading…</div>
                    ) : forms.length === 0 ? (
                        <div className="clearance-empty">
                            {isCoordinator
                                ? 'No clearance forms yet. Create a new request to get started.'
                                : 'No clearance forms assigned to you.'}
                        </div>
                    ) : (
                        <table className="clearance-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Employee</th>
                                    <th>Staff Code</th>
                                    <th>Reason</th>
                                    <th>Sections</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {forms.map((form) => (
                                    <tr key={form._id}>
                                        <td className="clearance-id-cell">{form.clearanceId || '—'}</td>
                                        <td>{form.employee?.name}</td>
                                        <td>{form.employee?.staffCode || '—'}</td>
                                        <td>{REASON_LABEL[form.clearance?.reason] || form.clearance?.reason}</td>
                                        <td>{sectionProgress(form)}</td>
                                        <td>
                                            <span className={`status-pill status-${form.status}`}>
                                                {STATUS_LABEL[form.status] || form.status}
                                            </span>
                                        </td>
                                        <td>{new Date(form.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <button className="btn-open" onClick={() => openDetail(form._id)}>
                                                Open
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                </>
                )}
            </div>

            {showCreate && (
                <CreateClearanceModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(newForm) => {
                        setShowCreate(false);
                        if (newForm?._id) openDetail(newForm._id);
                        else fetchForms();
                    }}
                />
            )}
        </div>
    );
};

export default ClearanceForm;
